import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Chart from 'chart.js/auto';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Types
interface OuraSleep {
  score?: number;
  efficiency?: number;
  total_sleep_duration?: number;
  contributors?: {
    efficiency?: number;
    deep_sleep?: number;
    rem_sleep?: number;
    total_sleep?: number;
  };
}

interface OuraActivity {
  score?: number;
  steps?: number;
  active_calories?: number;
}

interface OuraReadiness {
  score?: number;
  temperature_deviation?: number;
  contributors?: {
    resting_heart_rate?: number;
    body_temperature?: number;
    hrv_balance?: number;
  };
}

interface OuraDay {
  day: string;
  sleep?: OuraSleep;
  activity?: OuraActivity;
  readiness?: OuraReadiness;
}

interface MergedDay {
  day: string;
  sleep?: OuraSleep;
  activity?: OuraActivity;
  readiness?: OuraReadiness;
}

interface OuraData {
  daily_sleep: RawOuraDay[];
  daily_activity: RawOuraDay[];
  daily_readiness: RawOuraDay[];
  surgery_date?: string;
}

interface TimelinePoint {
  date: string;
  rhr: number | null;
  tempDev: number | null;
  sleepEff: number | null;
  recovery: number | null;
  hrv: number | null;
  deepSleep: number | null;
  period: 'baseline' | 'recovery';
}

interface BaselineStats {
  rhr: number | null;
  sleepEff: number | null;
}

interface RawOuraDay {
  day: string;
  [key: string]: any;
}

// Constants
const DATA_PATH = '/oura-summary.json';
const SURGERY_DATE = '2025-06-27';

function getDaysSinceSurgery(surgeryDate: string): number {
  const surgery = new Date(surgeryDate);
  const now = new Date();
  return Math.max(0, Math.floor((now.getTime() - surgery.getTime()) / (1000 * 60 * 60 * 24)));
}

function getBaselineStats(baseline: MergedDay[]): BaselineStats {
  const rhrs = baseline
    .map((d: MergedDay) => d.readiness?.contributors?.resting_heart_rate)
    .filter((val): val is number => typeof val === 'number' && isFinite(val));
  
  const sleepEffs = baseline
    .map((d: MergedDay) => (d.sleep?.contributors?.efficiency ?? d.sleep?.efficiency))
    .filter((val): val is number => typeof val === 'number' && isFinite(val));

  return {
    rhr: rhrs.length ? Math.round(rhrs.reduce((a, b) => a + b, 0) / rhrs.length) : null,
    sleepEff: sleepEffs.length ? Math.round(sleepEffs.reduce((a, b) => a + b, 0) / sleepEffs.length) : null,
  };
}

const OuraDashboard: React.FC = () => {
  const [ouraData, setOuraData] = useState<OuraData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mainTimelineRef = useRef<HTMLCanvasElement>(null);

  // Fetch data effect
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(DATA_PATH);
        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }
        const data = await response.json();
        setOuraData(data);
      } catch (e) {
        console.error('Error loading Oura data:', e);
        setError('Failed to load Oura data. Please ensure the data file exists.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Prepare chart data
  const surgeryDate = ouraData?.surgery_date || SURGERY_DATE;
  const daysSinceSurgery = getDaysSinceSurgery(surgeryDate);

  const allDays = ouraData ? Array.from(new Set([
    ...ouraData.daily_sleep.map((d: RawOuraDay) => d.day),
    ...ouraData.daily_activity.map((d: RawOuraDay) => d.day),
    ...ouraData.daily_readiness.map((d: RawOuraDay) => d.day),
  ])).sort() : [];

  const merged: Record<string, MergedDay> = {};
  if (ouraData) {
    allDays.forEach(day => {
      merged[day] = {
        day,
        sleep: ouraData.daily_sleep.find((d: RawOuraDay) => d.day === day) as OuraSleep | undefined,
        activity: ouraData.daily_activity.find((d: RawOuraDay) => d.day === day) as OuraActivity | undefined,
        readiness: ouraData.daily_readiness.find((d: RawOuraDay) => d.day === day) as OuraReadiness | undefined,
      };
    });
  }

  const timeline: TimelinePoint[] = allDays.map(day => {
    const d = merged[day];
    return {
      date: day,
      rhr: d?.readiness?.contributors?.resting_heart_rate ?? null,
      tempDev: d?.readiness?.temperature_deviation ?? null,
      sleepEff: (d?.sleep?.contributors?.efficiency ?? d?.sleep?.efficiency) ?? null,
      recovery: d?.readiness?.score ?? null,
      hrv: d?.readiness?.contributors?.hrv_balance ?? null,
      deepSleep: d?.sleep?.contributors?.deep_sleep ?? null,
      period: day < surgeryDate ? 'baseline' : 'recovery',
    };
  });

  const baseline = timeline.filter(d => d.period === 'baseline');
  const recovery = timeline.filter(d => d.period === 'recovery');
  const baselineStats = getBaselineStats(baseline.map(day => merged[day.date]));
  const lastRecovery = recovery.filter(d => d.sleepEff !== null).slice(-1)[0] || {};

  const rhrDeviation = lastRecovery.rhr && baselineStats.rhr ? lastRecovery.rhr - baselineStats.rhr : null;
  const sleepEffDeviation = lastRecovery.sleepEff && baselineStats.sleepEff ? lastRecovery.sleepEff - baselineStats.sleepEff : null;

  // Chart effect
  useEffect(() => {
    if (!mainTimelineRef.current || !timeline.length) return;

    // Destroy existing chart
    if ((mainTimelineRef.current as any)._chartInstance) {
      (mainTimelineRef.current as any)._chartInstance.destroy();
    }

    const chartLabels = timeline.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
    });
    
    const rhrData = timeline.map(d => d.rhr);
    const sleepEffData = timeline.map(d => d.sleepEff);
    const recoveryScoreData = timeline.map(d => d.recovery);

    // Find surgery index for vertical line
    const surgeryIndex = timeline.findIndex(d => d.date === surgeryDate);

    const chart = new Chart(mainTimelineRef.current, {
      type: 'line',
      data: {
        labels: chartLabels,
        datasets: [
          {
            label: 'Resting Heart Rate',
            data: rhrData,
            borderColor: '#e74c3c',
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            yAxisID: 'y',
            tension: 0.4,
            spanGaps: true,
          },
          {
            label: 'Sleep Efficiency (%)',
            data: sleepEffData,
            borderColor: '#9b59b6',
            backgroundColor: 'rgba(155, 89, 182, 0.1)',
            yAxisID: 'y1',
            tension: 0.4,
            spanGaps: true,
          },
          {
            label: 'Recovery Score',
            data: recoveryScoreData,
            borderColor: '#27ae60',
            backgroundColor: 'rgba(39, 174, 96, 0.1)',
            yAxisID: 'y1',
            tension: 0.4,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { 
          legend: { position: 'top' }
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: { display: true, text: 'Heart Rate (BPM)' },
            min: 30,
            max: 120,
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: { display: true, text: 'Efficiency % / Recovery Score' },
            min: 0,
            max: 100,
            grid: { drawOnChartArea: false },
          },
        },
      },
    });

    (mainTimelineRef.current as any)._chartInstance = chart;

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [timeline, surgeryDate]);

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        color: '#666',
        fontSize: '1.2rem'
      }}>
        <div>
          <div style={{ marginBottom: '1rem' }}>📊 Loading Oura data...</div>
          <div style={{ fontSize: '0.9rem', color: '#999' }}>
            Analyzing surgical recovery metrics
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !ouraData) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        background: '#f8f9fa',
        borderRadius: '8px',
        margin: '2rem',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ color: '#e74c3c', fontSize: '1.2rem', marginBottom: '1rem' }}>
          ⚠️ Unable to load Oura data
        </div>
        <div style={{ color: '#666', marginBottom: '1rem' }}>
          {error || 'No data found. Please ensure your Oura data file is available.'}
        </div>
        <div style={{ fontSize: '0.9rem', color: '#999' }}>
          Expected file: <code>{DATA_PATH}</code>
        </div>
      </div>
    );
  }

  // Determine overall status
  const getOverallStatus = () => {
    if (!recovery.length) return { status: 'info', message: 'Pre-surgery baseline established' };
    
    const alertConditions = [
      rhrDeviation && rhrDeviation > 10,
      lastRecovery.tempDev && Math.abs(lastRecovery.tempDev) > 0.5,
      lastRecovery.sleepEff && lastRecovery.sleepEff < 70,
      lastRecovery.recovery && lastRecovery.recovery < 65
    ].filter(Boolean);

    if (alertConditions.length >= 2) {
      return { status: 'danger', message: 'Multiple metrics concerning - contact medical team' };
    } else if (alertConditions.length === 1) {
      return { status: 'warning', message: 'Monitor closely - one metric outside normal range' };
    } else {
      return { status: 'normal', message: 'Recovery progressing normally' };
    }
  };

  const overallStatus = getOverallStatus();

  return (
    <>
      <Helmet>
        <title>Oura Recovery Dashboard | Surgical Monitoring</title>
        <meta name="description" content="Post-surgical recovery monitoring using Oura ring data" />
      </Helmet>

      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
        padding: '20px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '25px', color: 'white' }}>
            <h1 style={{ 
              fontSize: '2.2rem', 
              marginBottom: '10px', 
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)' 
            }}>
              🏥 Surgical Recovery Monitor
            </h1>
            <p>Real-time deviation tracking from your personal baseline</p>
          </div>

          {/* Surgery Info */}
          <div style={{ 
            background: recovery.length ? 'linear-gradient(135deg, #27ae60, #2ecc71)' : 'linear-gradient(135deg, #3498db, #74b9ff)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '25px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(39, 174, 96, 0.3)'
          }}>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '8px' }}>
              {recovery.length ? `Day ${daysSinceSurgery} Post-Surgery` : 'Pre-Surgery Baseline'}
            </div>
            <p>
              Surgery Date: {surgeryDate} | 
              Monitoring Period: {baseline.length ? `${baseline[0]?.date} - ${timeline[timeline.length - 1]?.date}` : 'Loading...'}
            </p>
          </div>

          {/* Status Overview */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
            marginBottom: '25px'
          }}>
            <div style={{
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
              borderLeft: `5px solid ${overallStatus.status === 'danger' ? '#e74c3c' : overallStatus.status === 'warning' ? '#f39c12' : '#27ae60'}`,
              background: overallStatus.status === 'danger' ? '#f8d7da' : overallStatus.status === 'warning' ? '#fff3cd' : '#d5f4e6',
              color: overallStatus.status === 'danger' ? '#721c24' : overallStatus.status === 'warning' ? '#856404' : '#0d5c26'
            }}>
              <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '10px', fontSize: '1.3rem' }}>
                  {overallStatus.status === 'danger' ? '🚨' : overallStatus.status === 'warning' ? '⚠️' : '✅'}
                </span>
                Recovery Status: {overallStatus.status === 'danger' ? 'Needs Attention' : overallStatus.status === 'warning' ? 'Monitor Closely' : 'Normal Progress'}
              </div>
              <p>{overallStatus.message}</p>
            </div>
          </div>

          {/* Current Metrics Display */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            marginBottom: '25px'
          }}>
            {/* RHR Card */}
            <div style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '20px', 
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)', 
              textAlign: 'center' 
            }}>
              <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '10px' }}>
                {recovery.length > 0 ? 'Current Heart Rate' : 'Baseline Heart Rate'}
              </div>
              <div style={{ 
                fontSize: '2.2rem', 
                fontWeight: 'bold', 
                marginBottom: '5px',
                color: recovery.length > 0 ? 
                  (rhrDeviation && Math.abs(rhrDeviation) > 10 ? '#e74c3c' : rhrDeviation && Math.abs(rhrDeviation) > 5 ? '#f39c12' : '#27ae60') : 
                  '#3498db'
              }}>
                {recovery.length > 0 ? 
                  (lastRecovery.rhr ? `${Math.round(lastRecovery.rhr)} BPM` : '—') :
                  (baselineStats.rhr ? `${baselineStats.rhr} BPM` : '—')
                }
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                {recovery.length > 0 ? 
                  (rhrDeviation !== null ? (rhrDeviation > 0 ? `+${rhrDeviation}` : rhrDeviation) + ' from baseline' : '—') :
                  'Baseline Average'
                }
              </div>
              <div style={{ fontSize: '0.85rem', color: '#95a5a6' }}>
                {recovery.length > 0 ? 
                  `Baseline: ${baselineStats.rhr ?? '—'} BPM | Alert: >${baselineStats.rhr ? baselineStats.rhr + 10 : '—'} BPM` :
                  (() => {
                    const validRhrs = baseline.map(d => d.rhr).filter((val): val is number => typeof val === 'number');
                    return validRhrs.length ? `Range: ${Math.min(...validRhrs)}-${Math.max(...validRhrs)} BPM` : 'Range: — BPM';
                  })()
                }
              </div>
            </div>

            {/* Temperature Monitoring Card */}
            <div style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '20px', 
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)', 
              textAlign: 'center',
              border: recovery.length > 0 && lastRecovery.tempDev && Math.abs(lastRecovery.tempDev) > 0.5 ? '2px solid #e74c3c' : 'none'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '10px' }}>
                {recovery.length > 0 ? '🌡️ Temperature Status' : '🌡️ Temperature Baseline'}
              </div>
              <div style={{ 
                fontSize: '2.2rem', 
                fontWeight: 'bold', 
                marginBottom: '5px',
                color: recovery.length > 0 ? 
                  (lastRecovery.tempDev && Math.abs(lastRecovery.tempDev) > 0.5 ? '#e74c3c' : lastRecovery.tempDev && Math.abs(lastRecovery.tempDev) > 0.3 ? '#f39c12' : '#27ae60') :
                  '#3498db'
              }}>
                {recovery.length > 0 ? 
                  (lastRecovery.tempDev !== null ? `${lastRecovery.tempDev > 0 ? '+' : ''}${lastRecovery.tempDev.toFixed(1)}°C` : '—') :
                  '±0.2°C'
                }
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                {recovery.length > 0 ? 
                  (lastRecovery.tempDev && Math.abs(lastRecovery.tempDev) > 0.5 ? '🚨 Fever Alert' : 
                   lastRecovery.tempDev && Math.abs(lastRecovery.tempDev) > 0.3 ? '⚠️ Monitor' : '✅ Normal') :
                  'Normal Variation'
                }
              </div>
              <div style={{ fontSize: '0.85rem', color: '#95a5a6' }}>
                {recovery.length > 0 ? 
                  'Normal: ±0.3°C | Fever Alert: >±0.5°C' :
                  'Established baseline for surgery monitoring'
                }
              </div>
            </div>

            {/* Sleep Efficiency Card */}
            <div style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '20px', 
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)', 
              textAlign: 'center' 
            }}>
              <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '10px' }}>
                {recovery.length > 0 ? 'Current Sleep Efficiency' : 'Baseline Sleep Efficiency'}
              </div>
              <div style={{ 
                fontSize: '2.2rem', 
                fontWeight: 'bold', 
                marginBottom: '5px',
                color: recovery.length > 0 ? 
                  (lastRecovery.sleepEff && lastRecovery.sleepEff < 70 ? '#e74c3c' : sleepEffDeviation && sleepEffDeviation < -5 ? '#f39c12' : '#27ae60') :
                  '#3498db'
              }}>
                {recovery.length > 0 ? 
                  (lastRecovery.sleepEff ? `${Math.round(lastRecovery.sleepEff)}%` : '—') :
                  (baselineStats.sleepEff ? `${baselineStats.sleepEff}%` : '—')
                }
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                {recovery.length > 0 ? 
                  (sleepEffDeviation !== null ? (sleepEffDeviation > 0 ? `+${sleepEffDeviation}` : sleepEffDeviation) + '% from baseline' : '—') :
                  'Baseline Average'
                }
              </div>
              <div style={{ fontSize: '0.85rem', color: '#95a5a6' }}>
                {recovery.length > 0 ? 
                  `Baseline: ${baselineStats.sleepEff ?? '—'}% | Alert: <70%` :
                  (() => {
                    const validSleepEffs = baseline.map(d => d.sleepEff).filter((val): val is number => typeof val === 'number');
                    return validSleepEffs.length ? `Range: ${Math.min(...validSleepEffs)}-${Math.max(...validSleepEffs)}%` : 'Range: —%';
                  })()
                }
              </div>
            </div>

            {/* Recovery Score Tracking Card */}
            <div style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '20px', 
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)', 
              textAlign: 'center',
              border: recovery.length > 0 && lastRecovery.recovery && lastRecovery.recovery < 65 ? '2px solid #e74c3c' : 'none'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '10px' }}>
                {recovery.length > 0 ? '🔄 Recovery Progress' : '🔄 Baseline Readiness'}
              </div>
              <div style={{ 
                fontSize: '2.2rem', 
                fontWeight: 'bold', 
                marginBottom: '5px',
                color: recovery.length > 0 ? 
                  (lastRecovery.recovery && lastRecovery.recovery < 65 ? '#e74c3c' : 
                   lastRecovery.recovery && lastRecovery.recovery < 75 ? '#f39c12' : '#27ae60') :
                  '#3498db'
              }}>
                {recovery.length > 0 ? 
                  (lastRecovery.recovery ? Math.round(lastRecovery.recovery) : '—') :
                  (baseline.length ? Math.round(baseline.reduce((sum, d) => sum + (d.recovery || 0), 0) / baseline.filter(d => d.recovery).length) : '—')
                }
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                {recovery.length > 0 ? 
                  (() => {
                    const recoveryDeviation = lastRecovery.recovery && baselineStats.rhr ? lastRecovery.recovery - 80 : null;
                    return recoveryDeviation !== null ? 
                      (recoveryDeviation > 0 ? `+${recoveryDeviation}` : recoveryDeviation) + ' from baseline' : '—';
                  })() :
                  'Baseline Average'
                }
              </div>
              <div style={{ fontSize: '0.85rem', color: '#95a5a6' }}>
                {recovery.length > 0 ? 
                  'Baseline: ~80 | Good: >75 | Alert: <65' :
                  'Optimal readiness for surgery'
                }
              </div>
            </div>

            {recovery.length > 0 && (
              <>
                {/* HRV Balance Card */}
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '20px', 
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)', 
                  textAlign: 'center' 
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '10px' }}>💓 HRV Balance</div>
                  <div style={{ 
                    fontSize: '2.2rem', 
                    fontWeight: 'bold', 
                    marginBottom: '5px',
                    color: '#3498db'
                  }}>
                    {lastRecovery.hrv ? Math.round(lastRecovery.hrv) : '—'}
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                    Stress/Recovery Indicator
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#95a5a6' }}>
                    Lower values = Stress/Inflammation
                  </div>
                </div>

                {/* Deep Sleep Card */}
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '20px', 
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)', 
                  textAlign: 'center' 
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '10px' }}>🛌 Deep Sleep</div>
                  <div style={{ 
                    fontSize: '2.2rem', 
                    fontWeight: 'bold', 
                    marginBottom: '5px',
                    color: lastRecovery.deepSleep && lastRecovery.deepSleep < 60 ? '#f39c12' : '#27ae60'
                  }}>
                    {lastRecovery.deepSleep ? `${Math.round(lastRecovery.deepSleep / 60)}h ${lastRecovery.deepSleep % 60}m` : '—'}
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                    Healing & Repair
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#95a5a6' }}>
                    Optimal: &gt;1h 30m for recovery
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Main Timeline Chart */}
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '25px', 
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)', 
            marginBottom: '25px' 
          }}>
            <div style={{ 
              fontSize: '1.3rem', 
              fontWeight: 600, 
              marginBottom: '20px', 
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                📊 Recovery Timeline: Baseline vs {recovery.length ? 'Post-Surgery' : 'Pre-Surgery'}
                {recovery.length > 0 && (
                  <span style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    marginLeft: '10px',
                    background: '#e3f2fd',
                    color: '#1976d2'
                  }}>
                    📈 Day {daysSinceSurgery}
                  </span>
                )}
              </div>
            </div>
            <div style={{ height: '320px' }}>
              <canvas ref={mainTimelineRef} style={{ width: '100%', height: '320px' }} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OuraDashboard;